import { useRef, useState, useEffect, useCallback } from "react";
import "./CurveChart.css";
import { useTranslation } from "react-i18next";

const PAD = { l: 48, r: 20, t: 36, b: 44 };
const CHART_H = 240;

function buildCurvePath(pts) {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const dx = (pts[i].x - pts[i - 1].x) * 0.4;
    d += ` C ${pts[i - 1].x + dx} ${pts[i - 1].y} ${pts[i].x - dx} ${pts[i].y} ${pts[i].x} ${pts[i].y}`;
  }
  return d;
}

// ── snap a raw number to param precision ────────────────────────────────
function snapToStep(raw, param) {
  const step = param.step ?? (param.max - param.min >= 100 ? 1 : 0.1);
  const decimals = step < 1 ? Math.round(-Math.log10(step)) : 0;
  const snapped = Math.round(raw / step) * step;
  const clamped = Math.min(param.max, Math.max(param.min, snapped));
  return parseFloat(clamped.toFixed(decimals));
}

function CurveChart({ param, values, n, onChange, isLightMode }) {
  const { t } = useTranslation();

  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const draggingRef = useRef(null);
  const [W, setW] = useState(700);
  const [activeIdx, setActiveIdx] = useState(null);

  // Determine theme class
  const themeClass = isLightMode ? "light-theme" : "";

  // ── keyboard typing state ───────────────────────────────────────────
  const [typingIdx, setTypingIdx] = useState(null);
  const [typingBuffer, setTypingBuffer] = useState("");
  const [inputError, setInputError] = useState(false);

  useEffect(() => {
    const obs = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w > 0) setW(w);
    });
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  // ── coordinate helpers ─────────────────────────
  const valToY = useCallback(
    (val) =>
      PAD.t +
      (1 - (val - param.min) / (param.max - param.min)) *
        (CHART_H - PAD.t - PAD.b),
    [param],
  );

  const yToVal = useCallback(
    (svgY) => {
      const ratio = 1 - (svgY - PAD.t) / (CHART_H - PAD.t - PAD.b);
      const raw = param.min + ratio * (param.max - param.min);
      return Math.min(
        param.max,
        Math.max(param.min, Math.round(raw * 10) / 10),
      );
    },
    [param],
  );

  const idxToX = useCallback(
    (i) => PAD.l + (i / (n - 1)) * (W - PAD.l - PAD.r),
    [W, n],
  );

  const clientYToSvgY = useCallback((clientY) => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const pt = svg.createSVGPoint();
    pt.x = 0;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    return svgPt.y;
  }, []);

  // ──  commit typed value ──────────────────────────────────────────────
  const commitTyping = useCallback(
    (buffer, idx) => {
      const raw = parseFloat(buffer);
      if (!isNaN(raw)) {
        onChange(idx, snapToStep(raw, param));
        setTypingBuffer("");
        setTypingIdx(null);
        setInputError(false);
      } else {
        setInputError(true);
        setTimeout(() => setInputError(false), 600);
      }
    },
    [param, onChange],
  );

  // ── global keydown for selected point ───────────────────────────────
  useEffect(() => {
    if (activeIdx === null && typingIdx === null) return;
    const focusedIdx = typingIdx !== null ? typingIdx : activeIdx;

    const onKeyDown = (e) => {
      // Arrow nudge
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        const step = param.step ?? (param.max - param.min >= 100 ? 1 : 0.1);
        const decimals = step < 1 ? Math.round(-Math.log10(step)) : 0;
        const current = values[focusedIdx];
        const next = parseFloat(
          Math.min(
            param.max,
            Math.max(param.min, current + (e.key === "ArrowUp" ? step : -step)),
          ).toFixed(decimals),
        );
        onChange(focusedIdx, next);
        return;
      }
      // Escape → deselect
      if (e.key === "Escape") {
        setTypingBuffer("");
        setTypingIdx(null);
        setActiveIdx(null);
        return;
      }
      // Tab → commit + move to next/prev point
      if (e.key === "Tab") {
        e.preventDefault();
        if (typingIdx !== null && typingBuffer !== "") {
          commitTyping(typingBuffer, typingIdx);
        }
        const next = e.shiftKey
          ? Math.max(0, focusedIdx - 1)
          : Math.min(n - 1, focusedIdx + 1);
        setActiveIdx(next);
        setTypingIdx(null);
        setTypingBuffer("");
        return;
      }
      // Enter → commit
      if (e.key === "Enter") {
        if (typingBuffer !== "") commitTyping(typingBuffer, focusedIdx);
        return;
      }
      // Backspace
      if (e.key === "Backspace") {
        e.preventDefault();
        setTypingBuffer((b) => b.slice(0, -1));
        if (typingIdx === null) setTypingIdx(focusedIdx);
        return;
      }
      // Digits / dot / minus
      if (/^[\d.\-]$/.test(e.key)) {
        e.preventDefault();
        if (e.key === "." && typingBuffer.includes(".")) return;
        if (e.key === "-" && typingBuffer.length > 0) return;
        setTypingBuffer((b) => b + e.key);
        if (typingIdx === null) setTypingIdx(focusedIdx);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    activeIdx,
    typingIdx,
    typingBuffer,
    values,
    param,
    n,
    onChange,
    commitTyping,
  ]);

  // ── drag handlers ──────────────────────────────
  const handlePointerDown = useCallback((e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = idx;
    setActiveIdx(idx);
    // Clear any pending typing when starting a new drag
    setTypingBuffer("");
    setTypingIdx(null);
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, []);

  const handlePointerMove = useCallback(
    (e) => {
      if (draggingRef.current === null) return;
      e.preventDefault();
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const svgY = clientYToSvgY(clientY);
      const newVal = yToVal(svgY);
      onChange(draggingRef.current, newVal);
    },
    [clientYToSvgY, yToVal, onChange],
  );

  const handlePointerUp = useCallback(() => {
    draggingRef.current = null;
    // activeIdx intentionally kept so keyboard works after drag
  }, []);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    svg.addEventListener("pointermove", handlePointerMove);
    svg.addEventListener("pointerup", handlePointerUp);
    svg.addEventListener("pointercancel", handlePointerUp);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      svg.removeEventListener("pointermove", handlePointerMove);
      svg.removeEventListener("pointerup", handlePointerUp);
      svg.removeEventListener("pointercancel", handlePointerUp);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  // ── render data ────────────────────────────────
  const pts = values.map((v, i) => ({ x: idxToX(i), y: valToY(v) }));
  const linePath = buildCurvePath(pts);
  const areaPath =
    linePath +
    ` L ${pts[pts.length - 1].x} ${CHART_H - PAD.b} L ${pts[0].x} ${CHART_H - PAD.b} Z`;

  const gridCount = 4;
  const gridLineColor = isLightMode
    ? "rgba(0,0,0,0.1)"
    : "rgba(255,255,255,0.1)";

  return (
    <div ref={wrapRef} className={`curve-wrap ${themeClass}`}>
      <div className="curve-keyboard-hint">
        {activeIdx !== null ? (
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> {t("simulationModal.curveChart.adjust")} &nbsp;|&nbsp;
            <kbd>Tab</kbd> {t("simulationModal.curveChart.nextPoint")}{" "}
            &nbsp;|&nbsp;{" "}
            {t("simulationModal.curveChart.typeNumberHint", {
              enterKey: t("simulationModal.curveChart.enterKey"),
            })
              .split(t("simulationModal.curveChart.enterKey"))
              .reduce((acc, part, i, arr) => {
                acc.push(part);
                if (i < arr.length - 1) {
                  acc.push(
                    <kbd key={i}>
                      {t("simulationModal.curveChart.enterKey")}
                    </kbd>,
                  );
                }
                return acc;
              }, [])}{" "}
            &nbsp;|&nbsp;
            <kbd>{t("simulationModal.curveChart.escapeKey")}</kbd>{" "}
            {t("simulationModal.curveChart.deselect")}
          </span>
        ) : (
          <span>{t("simulationModal.curveChart.clickOrDragHint")}</span>
        )}
      </div>

      <svg
        ref={svgRef}
        width={W}
        height={CHART_H}
        viewBox={`0 0 ${W} ${CHART_H}`}
        style={{ display: "block", userSelect: "none", touchAction: "none" }}
      >
        {Array.from({ length: gridCount + 1 }).map((_, i) => {
          const y = PAD.t + (i / gridCount) * (CHART_H - PAD.t - PAD.b);
          const val = Math.round(
            param.max - (i / gridCount) * (param.max - param.min),
          );
          return (
            <g key={i}>
              <line
                x1={PAD.l}
                y1={y}
                x2={W - PAD.r}
                y2={y}
                stroke={gridLineColor}
                strokeWidth="1"
              />
              <text
                x={PAD.l - 8}
                y={y + 4}
                className="chart-axis-text"
                fontSize="10"
                textAnchor="end"
              >
                {val}
              </text>
            </g>
          );
        })}

        {/* ── x-axis labels  ── */}
        {pts.map((pt, i) => (
          <text
            key={i}
            x={pt.x}
            y={CHART_H - PAD.b + 18}
            className="chart-axis-text"
            fontSize="10"
            textAnchor="middle"
          >
            {`t+${i + 1}h`}
          </text>
        ))}

        <path d={areaPath} fill={param.color} opacity="0.15" />
        <path
          d={linePath}
          fill="none"
          stroke={param.color}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* ── active vertical guide  ── */}
        {activeIdx !== null && (
          <line
            x1={pts[activeIdx].x}
            y1={PAD.t}
            x2={pts[activeIdx].x}
            y2={CHART_H - PAD.b}
            stroke={param.color}
            strokeWidth="1"
            strokeDasharray="4 3"
            opacity="0.5"
          />
        )}

        {/* ── points — label extended for typing ── */}
        {pts.map((pt, i) => {
          const isActive = activeIdx === i;
          const isTyping = typingIdx === i;

          return (
            <g key={i}>
              {isTyping && (
                <rect
                  x={pt.x - 28}
                  y={pt.y - 36}
                  width={56}
                  height={18}
                  rx={4}
                  fill={inputError ? "#ff4444" : param.color}
                  opacity="0.85"
                  style={{ pointerEvents: "none" }}
                />
              )}

              <text
                x={pt.x}
                y={pt.y - (isActive || isTyping ? 20 : 15)}
                fill={
                  isTyping
                    ? inputError
                      ? "#ff4444"
                      : "#fff"
                    : isActive
                      ? param.color
                      : "var(--text-color)"
                }
                fontSize={isActive || isTyping ? "12" : "10.5"}
                fontWeight="600"
                textAnchor="middle"
                style={{ pointerEvents: "none" }}
              >
                {isTyping
                  ? typingBuffer === ""
                    ? "|"
                    : typingBuffer + "|"
                  : `${Math.round(values[i] * 10) / 10}${t(param.unitKey)}`}
              </text>

              {isActive && (
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r="11"
                  fill={param.color}
                  opacity="0.25"
                  style={{ pointerEvents: "none" }}
                />
              )}

              <circle
                cx={pt.x}
                cy={pt.y}
                r="16"
                fill="transparent"
                style={{ cursor: isActive ? "grabbing" : "grab" }}
                onPointerDown={(e) => handlePointerDown(e, i)}
                data-testid={`curve-chart-point-${i}`}
              />
              <circle
                cx={pt.x}
                cy={pt.y}
                r={isActive ? 7 : 5}
                fill={param.color}
                stroke="#fff"
                strokeWidth={isActive ? 2.5 : 2}
                style={{ pointerEvents: "none", transition: "r 0.1s" }}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default CurveChart;
